import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import GridCard from "../../widgets/GridCard.jsx";

const dateFormat = new Intl.DateTimeFormat("default", {
  dateStyle: "short",
  timeStyle: "short",
});

const useStyles = makeStyles((theme) => ({
  content: {
    padding: theme.spacing(0, 2, 2, 2),
  },
  label: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    letterSpacing: "0.02em",
    textTransform: "uppercase",
  },
  value: {
    color: theme.palette.text.primary,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  // Fields the inspector left blank read lighter than recorded ones, so the
  // eye skips them rather than reading "Not recorded" as content.
  missing: {
    color: theme.palette.text.disabled,
    fontStyle: "italic",
  },
}));

// Inspections created before a field existed simply omit it, which is a
// different thing from the inspector leaving it blank on purpose. Both read as
// "Not recorded" here, because the distinction is not one we store.
const hasValue = (value) =>
  value !== null && value !== undefined && value !== "";

const formatLocality = (city, state) => {
  const parts = [city, state].filter((part) => part);

  return parts.length > 0 ? parts.join(", ") : null;
};

const formatCreatedAt = (createdAt) => {
  if (!createdAt || typeof createdAt.toDate !== "function") {
    return null;
  }

  return dateFormat.format(createdAt.toDate());
};

function DetailField(props) {
  const { label, value, width } = props;
  const classes = useStyles();
  const recorded = hasValue(value);

  return (
    <Grid item xs={12} sm={width ?? 6}>
      <Typography className={classes.label} variant="caption" display="block">
        {label}
      </Typography>
      <Typography
        className={recorded ? classes.value : classes.missing}
        variant="body2"
      >
        {recorded ? value : "Not recorded"}
      </Typography>
    </Grid>
  );
}

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  width: PropTypes.number,
};

function InspectionDetailCard(props) {
  const { inspection, loading, error } = props;
  const classes = useStyles();

  return (
    <GridCard title="Job Details" loading={loading} error={error}>
      {inspection && (
        <Grid container spacing={2} className={classes.content}>
          <DetailField label="Location" value={inspection.location} />
          <DetailField
            label="City / State"
            value={formatLocality(inspection.city, inspection.state)}
          />
          <DetailField label="Customer" value={inspection.customer} />
          <DetailField label="Job Number" value={inspection.jobNumber} />
          <DetailField label="Inspector" value={inspection.inspector} />
          <DetailField
            label="Date"
            value={formatCreatedAt(inspection.createdAt)}
          />
          <DetailField label="Scope of Work" value={inspection.scopeOfWork} />
          <DetailField
            label="Square Footage"
            value={inspection.squareFootage}
          />
          <DetailField
            label="Description"
            value={inspection.jobDescription}
            width={12}
          />
        </Grid>
      )}
    </GridCard>
  );
}

InspectionDetailCard.propTypes = {
  inspection: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

export default InspectionDetailCard;
